import React, { useState } from 'react';
import { Plus, Trash2, UserPlus, Mail, Users, Building2 } from 'lucide-react';

export default function TeamMemberInvitationStep({ wizardData, updateWizardData, setError }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    roleId: '',
    department: ''
  });
  const [bulkEmails, setBulkEmails] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);

  // Add single team member
  const handleAddMember = () => {
    // Validation
    if (!formData?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      setError?.('Please enter a valid email address');
      return;
    }

    if (!formData?.roleId) {
      setError?.('Please select a role for this team member');
      return;
    }

    // Check for duplicate email
    const isDuplicate = wizardData?.teamMembers?.some(
      member => member?.email?.toLowerCase() === formData?.email?.toLowerCase()
    );

    if (isDuplicate) {
      setError?.('This email has already been added');
      return;
    }

    // Add member
    const newMember = {
      id: `temp_${Date.now()}`,
      email: formData?.email,
      firstName: formData?.firstName || '',
      lastName: formData?.lastName || '',
      roleId: formData?.roleId,
      department: formData?.department || ''
    };

    updateWizardData('teamMembers', [...wizardData?.teamMembers, newMember]);

    // Reset form
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      roleId: '',
      department: ''
    });
    setShowForm(false);
    setError?.('');
  };

  // Bulk add team members
  const handleBulkAdd = () => {
    const emails = bulkEmails?.split(/[\n,;]/)?.map(email => email?.trim())?.filter(email => email?.length > 0);

    if (emails?.length === 0) {
      setError?.('Please enter at least one email address');
      return;
    }

    if (!formData?.roleId) {
      setError?.('Please select a role for bulk invitations');
      return;
    }

    const validEmails = emails?.filter(email => 
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)
    );

    if (validEmails?.length === 0) {
      setError?.('No valid email addresses found');
      return;
    }

    // Check for duplicates
    const existingEmails = new Set(wizardData.teamMembers.map(m => m.email.toLowerCase()));
    const newMembers = validEmails?.filter(email => !existingEmails?.has(email?.toLowerCase()))?.map(email => ({
        id: `temp_${Date.now()}_${Math.random()}`,
        email,
        firstName: '',
        lastName: '',
        roleId: formData?.roleId,
        department: formData?.department || ''
      }));

    if (newMembers?.length === 0) {
      setError?.('All email addresses have already been added');
      return;
    }

    updateWizardData('teamMembers', [...wizardData?.teamMembers, ...newMembers]);

    // Show success message
    setError?.('');
    alert(`Successfully added ${newMembers?.length} team member(s)`);

    // Reset form
    setBulkEmails('');
    setShowBulkInput(false);
    setFormData({ ...formData, roleId: '', department: '' });
  };

  // Delete team member
  const handleDeleteMember = (memberId) => {
    const updatedMembers = wizardData?.teamMembers?.filter(m => m?.id !== memberId);
    updateWizardData('teamMembers', updatedMembers);
  };

  // Get role display name
  const getRoleDisplayName = (roleId) => {
    const role = wizardData?.roles?.find(r => r?.tempId === roleId);
    return role?.displayName || 'Unknown Role';
  };

  // Group members by role
  const membersByRole = wizardData?.teamMembers?.reduce((acc, member) => {
    const roleName = getRoleDisplayName(member?.roleId);
    if (!acc?.[roleName]) {
      acc[roleName] = [];
    }
    acc?.[roleName]?.push(member);
    return acc;
  }, {});

  if (wizardData?.roles?.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No roles available</h3>
        <p className="text-gray-600">Please create roles in Step 1 before inviting team members</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Invite Team Members</h2>
        <p className="mt-2 text-gray-600">
          Add your team members by email and assign them appropriate roles. They'll receive invitation emails to join the CRM.
        </p>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        <button
          onClick={() => {
            setShowForm(!showForm);
            setShowBulkInput(false);
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Single Member
        </button>
        <button
          onClick={() => {
            setShowBulkInput(!showBulkInput);
            setShowForm(false);
          }}
          className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Bulk Invite
        </button>
      </div>
      {/* Single Member Form */}
      {showForm && (
        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-4">Add Team Member</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  placeholder="john.doe@company.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign Role *
                </label>
                <select
                  value={formData?.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a role...</option>
                  {wizardData?.roles?.map(role => (
                    <option key={role?.tempId} value={role?.tempId}>
                      {role?.displayName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name (Optional)
                </label>
                <input
                  type="text"
                  value={formData?.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e?.target?.value })}
                  placeholder="John"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name (Optional)
                </label>
                <input
                  type="text"
                  value={formData?.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e?.target?.value })}
                  placeholder="Doe"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department (Optional)
                </label>
                <input
                  type="text"
                  value={formData?.department}
                  onChange={(e) => setFormData({ ...formData, department: e?.target?.value })}
                  placeholder="Sales"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk Invite Form */}
      {showBulkInput && (
        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-4">Bulk Invite Team Members</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Addresses *
              </label>
              <textarea
                value={bulkEmails}
                onChange={(e) => setBulkEmails(e?.target?.value)}
                placeholder="Enter email addresses (one per line, or comma/semicolon separated)&#10;john.doe@company.com&#10;jane.smith@company.com&#10;bob.johnson@company.com"
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate multiple emails with new lines, commas, or semicolons
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign Role to All *
                </label>
                <select
                  value={formData?.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a role...</option>
                  {wizardData?.roles?.map(role => (
                    <option key={role?.tempId} value={role?.tempId}>
                      {role?.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department (Optional)
                </label>
                <input
                  type="text"
                  value={formData?.department}
                  onChange={(e) => setFormData({ ...formData, department: e?.target?.value })}
                  placeholder="e.g., Sales, Marketing"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowBulkInput(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAdd}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Add All Members
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Team Members List */}
      {wizardData?.teamMembers?.length > 0 ? (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Team Members ({wizardData?.teamMembers?.length})
          </h3>
          
          {/* Group by Role */}
          <div className="space-y-6">
            {Object.entries(membersByRole)?.map(([roleName, members]) => (
              <div key={roleName}>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  {roleName} ({members?.length})
                </h4>
                <div className="space-y-2">
                  {members?.map(member => (
                    <div
                      key={member?.id}
                      className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {member?.firstName || member?.lastName 
                              ? `${member?.firstName} ${member?.lastName}`?.trim()
                              : 'Name not provided'
                            }
                          </p>
                          <p className="text-sm text-gray-600">{member?.email}</p>
                          {member?.department && (
                            <p className="text-xs text-gray-500 flex items-center mt-1">
                              <Building2 className="w-3 h-3 mr-1" />
                              {member?.department}
                            </p>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleDeleteMember(member?.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No team members added yet</h3>
          <p className="text-gray-600 mb-4">
            Start by adding team members individually or use bulk invite
          </p>
        </div>
      )}
      {/* Help Section */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="font-semibold text-purple-900 mb-2">📧 Invitation Process</h4>
        <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
          <li>Team members will receive email invitations to join the CRM</li>
          <li>They'll need to create a password to activate their account</li>
          <li>Roles and permissions will be automatically assigned upon activation</li>
          <li>You can modify roles and permissions later in User Management</li>
        </ul>
      </div>
    </div>
  );
}